/* eslint-disable import/no-cycle */

import '../Goals.css';

import { faArrowRight } from '@fortawesome/pro-light-svg-icons';
import { faCircle } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheckCircle } from '@fortawesome/sharp-solid-svg-icons';
import { useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { AuthContext } from '../../shared/context/auth-context';
import { WhiteBackgroundModal } from '../../shared/modal/whitePopUp/WhiteBackgroundModal';
import {
  useFetchSelectedGoalsQuery,
  useAddSelectedGoalMutation,
  useDeleteSelectedGoalMutation,
} from '../goalsApiSlice';
import { selectableGoals } from '../types';

interface GoalSelectorProps {
  handleClose: () => void;
}

function GoalSelector({ handleClose }: GoalSelectorProps) {
  const auth = useContext(AuthContext);
  const { t } = useTranslation();
  const { data: savedGoalsData } = useFetchSelectedGoalsQuery(auth.token);
  const [addSelectedGoalAPI] = useAddSelectedGoalMutation();
  const [deleteSelectedGoalAPI] = useDeleteSelectedGoalMutation();
  const activeIds: string[] = savedGoalsData?.selectedGoals ?? [];
  const [clickedGoal, setClickedGoal] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [pendingId, setPendingId] = useState<string | null>(null);

  const handleAddGoal = async (goalId: string) => {
    try {
      await addSelectedGoalAPI({
        token: auth.token,
        goalId,
      });
    } catch {
      setError(t('goals.unableToAddGoal'));
    } finally {
      setLoading(false);
      setPendingId(null);
    }
  };

  const handleDeleteGoal = async (goalId: string) => {
    try {
      await deleteSelectedGoalAPI({
        token: auth.token,
        goalId,
      });
    } catch {
      setError(t('G035'));
    } finally {
      setLoading(false);
      setPendingId(null);
    }
  };

  const handleClick = (goalId: string) => {
    setLoading(true);
    setPendingId(goalId);
    setError(null);
    setClickedGoal(goalId);
    if (activeIds.includes(goalId)) {
      if (activeIds.length === 1) {
        setError(t('G053'));
        setLoading(false);
        setPendingId(null);
      } else {
        handleDeleteGoal(goalId);
      }
    } else if (activeIds.length >= 2) {
      setLoading(false);
      setPendingId(null);
      setError(t('G023'));
    } else {
      handleAddGoal(goalId);
    }
  };

  const saveChanges = () => {
    handleClose();
  };

  return (
    <>
      <WhiteBackgroundModal
        hasCloseIcon
        id="goal-selector"
        title={{ text: t('G011'), showTitle: true }}
        submitButton={{
          text: t('M018'),
          icon: faArrowRight,
          onClick: saveChanges,
        }}
        handleClose={handleClose}
        dialogTitle={t('modal.goals.info')}
      >
        <fieldset className="btns-container fs-exclude">
          <legend className="visuallyHidden" id="goal-selector-legend">
            {t('G011')}
          </legend>
          {selectableGoals.map((goal) => {
            const isActive = activeIds.includes(goal);
            return (
              <div key={goal}>
                <button
                  type="button"
                  onClick={() => handleClick(goal)}
                  disabled={loading && pendingId !== goal}
                  className={`transparent-button goal-btn  ${
                    clickedGoal === goal && error ? 'error' : ''
                  } ${isActive ? 'active' : ''} `}
                  aria-labelledby={`goal-${goal}-status`}
                >
                  <span id={`goal-${goal}-status`} hidden>
                    {t(`selectedGoals.goal_${goal}`)}
                    {isActive
                      ? t('goal.addGoalsModal.isActive')
                      : t('goal.addGoalsModal.isNotActive')}
                  </span>
                  <p aria-hidden="true">{t(`selectedGoals.goal_${goal}`)}</p>
                  <FontAwesomeIcon
                    icon={isActive ? faCheckCircle : faCircle}
                    className="icon"
                    color="var(--color-secondary-orange)"
                  />
                </button>

                {clickedGoal === goal && error && (
                  <div className="goal-error" role="alert">
                    {error}
                  </div>
                )}
              </div>
            );
          })}
        </fieldset>
      </WhiteBackgroundModal>
      {error && (
        <div role="alert" aria-live="assertive" className="sr-only">
          {error}
        </div>
      )}
    </>
  );
}

export default GoalSelector;
